/**
 * LocationPicker Component
 * Interactive map for selecting location with drag-and-drop marker
 */

import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LocationPicker.css';

// Fix default marker icon issue with Leaflet in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Component to handle map clicks and update marker position
const LocationMarker = ({ position, setPosition }) => {
  const markerRef = useRef(null);

  useMapEvents({
    click(e) {
      setPosition({
        lat: e.latlng.lat,
        lng: e.latlng.lng,
      });
    },
  });

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        setPosition({
          lat: newPos.lat,
          lng: newPos.lng,
        });
      }
    },
  };

  return position ? (
    <Marker
      position={[position.lat, position.lng]}
      draggable={true}
      eventHandlers={eventHandlers}
      ref={markerRef}
    />
  ) : null;
};

const LocationPicker = ({ latitude, longitude, onLocationChange, compact = false }) => {
  const [position, setPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([40.7128, -74.006]); // Default: New York
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  // Initialize position from props
  useEffect(() => {
    if (latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setPosition({ lat, lng });
        setMapCenter([lat, lng]);
      }
    }
  }, [latitude, longitude]);

  // Update parent component when position changes
  useEffect(() => {
    if (position && onLocationChange) {
      onLocationChange(position.lat, position.lng);
    }
  }, [position, onLocationChange]);

  const handleUpdatePosition = (newPosition) => {
    setPosition(newPosition);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (geoPosition) => {
        const newPos = {
          lat: geoPosition.coords.latitude,
          lng: geoPosition.coords.longitude,
        };
        setPosition(newPos);
        setMapCenter([newPos.lat, newPos.lng]);
        setIsLoadingLocation(false);
      },
      (error) => {
        alert('Unable to retrieve your location: ' + error.message);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  };

  return (
    <div className={`location-picker ${compact ? 'location-picker-compact' : ''}`}>
      <div className="location-picker-header">
        {!compact && <h3>Select Location on Map</h3>}
        <button
          type="button"
          className="btn-current-location"
          onClick={handleUseCurrentLocation}
          disabled={isLoadingLocation}
        >
          {isLoadingLocation ? '📍 Loading...' : '📍 Use Current Location'}
        </button>
        {compact && position && (
          <span className="compact-coords">
            {position.lat.toFixed(4)}, {position.lng.toFixed(4)}
          </span>
        )}
      </div>

      {!compact && (
        <div className="location-picker-info">
          {position ? (
            <p>
              <strong>Selected Location:</strong> Latitude: {position.lat.toFixed(6)}, Longitude:{' '}
              {position.lng.toFixed(6)}
            </p>
          ) : (
            <p className="help-text">
              Click anywhere on the map or drag the marker to set your location
            </p>
          )}
        </div>
      )}

      <div className="map-container">
        <MapContainer
          center={mapCenter}
          zoom={compact ? 12 : 13}
          style={{ height: compact ? '180px' : '400px', width: '100%', borderRadius: '8px' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationMarker position={position} setPosition={handleUpdatePosition} />
        </MapContainer>
      </div>

      {!compact && (
        <div className="location-picker-footer">
          <p className="help-text">
            💡 Tip: You can click on the map to place the marker, or drag the marker to fine-tune
            the location
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
